/**
 * Sign-in reducer — pure function: `(state, action) => state`.
 *
 * Outer switch is on `state.step` so each step reads as a self-contained
 * node in the state machine: "when we are HERE, these transitions are legal."
 *
 * Returns the same reference when the action is not valid in the current step.
 * FlowStore uses reference equality to skip notification on no-op transitions.
 */

import type { SignInState, SignInAction, SignInUserData } from './types';

export const INITIAL_STATE: SignInState = { step: 'IDLE', identifier: null, error: null };

export function signInReducer(state: SignInState, action: SignInAction): SignInState {
  if (action.type === 'RESET') return INITIAL_STATE;

  switch (state.step) {
    // ── IDLE ──────────────────────────────────────────────────────────────
    case 'IDLE':
      switch (action.type) {
        case 'SUBMIT_EMAIL':
          return { step: 'LOOKING_UP_EMAIL', identifier: action.identifier };
        default:
          return state;
      }

    // ── LOOKING_UP_EMAIL ─────────────────────────────────────────────────
    case 'LOOKING_UP_EMAIL':
      switch (action.type) {
        case 'EMAIL_LOOKUP_OK': {
          const userData: SignInUserData = {
            firstName: action.clientApp.firstName,
            authMethods: action.clientApp.authMethods,
          };
          switch (action.cognitoCode) {
            case 'UserNotConfirmedException':
              return { step: 'UNCONFIRMED', identifier: state.identifier, error: null };
            case 'PasswordResetRequiredException':
              return { step: 'RESET_REQUIRED', identifier: state.identifier, userData, error: null };
            case 'AccountReady':
              return action.clientApp.exists && action.clientApp.authMethods.length > 0
                ? { step: 'ACCOUNT_READY', identifier: state.identifier, userData, error: null }
                : { step: 'USER_NOT_FOUND', identifier: state.identifier, userData };
            default:
              return { step: 'USER_NOT_FOUND', identifier: state.identifier, userData };
          }
        }
        case 'EMAIL_LOOKUP_FAIL':
          return { step: 'IDLE', identifier: state.identifier, error: action.error };
        case 'BACK':
          return { step: 'IDLE', identifier: state.identifier, error: null };
        default:
          return state;
      }

    // ── ACCOUNT_READY ────────────────────────────────────────────────────
    case 'ACCOUNT_READY':
      switch (action.type) {
        case 'SUBMIT_EMAIL':
          return { step: 'LOOKING_UP_EMAIL', identifier: action.identifier };
        case 'SUBMIT_PASSWORD':
          return { step: 'SIGNING_IN', identifier: state.identifier, userData: state.userData };
        case 'SUBMIT_RESET_PASSWORD':
          return {
            step: 'SENDING_RESET_CODE',
            identifier: state.identifier,
            userData: state.userData,
            pendingNewPassword: action.password,
          };
        case 'BACK':
          return { step: 'IDLE', identifier: state.identifier, error: null };
        default:
          return state;
      }

    // ── USER_NOT_FOUND ───────────────────────────────────────────────────
    case 'USER_NOT_FOUND':
      switch (action.type) {
        case 'SUBMIT_EMAIL':
          return { step: 'LOOKING_UP_EMAIL', identifier: action.identifier };
        case 'BACK':
          return { step: 'IDLE', identifier: state.identifier, error: null };
        default:
          return state;
      }

    // ── UNCONFIRMED ──────────────────────────────────────────────────────
    case 'UNCONFIRMED':
      switch (action.type) {
        case 'SUBMIT_EMAIL':
          return { step: 'LOOKING_UP_EMAIL', identifier: action.identifier };
        case 'SUBMIT_VERIFICATION':
          return { step: 'VERIFYING_SIGN_UP', identifier: state.identifier };
        case 'RESEND_CODE_FAIL':
          return { ...state, error: action.error };
        default:
          return state;
      }

    // ── RESET_REQUIRED ───────────────────────────────────────────────────
    case 'RESET_REQUIRED':
      switch (action.type) {
        case 'SUBMIT_EMAIL':
          return { step: 'LOOKING_UP_EMAIL', identifier: action.identifier };
        case 'SUBMIT_RESET_PASSWORD':
          return {
            step: 'SENDING_RESET_CODE',
            identifier: state.identifier,
            userData: state.userData,
            pendingNewPassword: action.password,
          };
        case 'BACK':
          return { step: 'IDLE', identifier: state.identifier, error: null };
        default:
          return state;
      }

    // ── SIGNING_IN ───────────────────────────────────────────────────────
    case 'SIGNING_IN':
      switch (action.type) {
        case 'SIGN_IN_OK':
        case 'SIGN_IN_NEEDS_PHONE':
          return { step: 'COMPLETE' };
        case 'SIGN_IN_FAIL':
          switch (action.stepOverride) {
            case 'UNCONFIRMED':
              return { step: 'UNCONFIRMED', identifier: state.identifier, error: null };
            case 'RESET_REQUIRED':
              return { step: 'RESET_REQUIRED', identifier: state.identifier, userData: state.userData, error: null };
            default:
              return { step: 'ACCOUNT_READY', identifier: state.identifier, userData: state.userData, error: action.error };
          }
        case 'BACK':
          return { step: 'IDLE', identifier: state.identifier, error: null };
        default:
          return state;
      }

    // ── SENDING_RESET_CODE ───────────────────────────────────────────────
    case 'SENDING_RESET_CODE':
      switch (action.type) {
        case 'RESET_CODE_OK':
          return {
            step: 'RESET_CODE_SENT',
            identifier: state.identifier,
            pendingNewPassword: state.pendingNewPassword,
            error: null,
          };
        case 'RESET_CODE_FAIL':
          return {
            step: 'RESET_REQUIRED',
            identifier: state.identifier,
            userData: state.userData,
            error: action.error,
          };
        case 'BACK':
          return { step: 'IDLE', identifier: state.identifier, error: null };
        default:
          return state;
      }

    // ── RESET_CODE_SENT ──────────────────────────────────────────────────
    case 'RESET_CODE_SENT':
      switch (action.type) {
        case 'SUBMIT_VERIFICATION':
          return {
            step: 'VERIFYING_RESET',
            identifier: state.identifier,
            pendingNewPassword: state.pendingNewPassword,
          };
        case 'RESEND_CODE_FAIL':
          return { ...state, error: action.error };
        default:
          return state;
      }

    // ── VERIFYING_SIGN_UP ────────────────────────────────────────────────
    case 'VERIFYING_SIGN_UP':
      switch (action.type) {
        case 'VERIFICATION_OK':
          return { step: 'COMPLETE' };
        case 'SIGN_UP_CONFIRMED':
          return {
            step: 'ACCOUNT_READY',
            identifier: state.identifier,
            userData: { authMethods: ['password'] },
            error: null,
          };
        case 'VERIFICATION_FAIL':
          return { step: 'UNCONFIRMED', identifier: state.identifier, error: action.error };
        default:
          return state;
      }

    // ── VERIFYING_RESET ──────────────────────────────────────────────────
    case 'VERIFYING_RESET':
      switch (action.type) {
        // `needs_phone` from a token exchange hands off to phone collection
        // via the event bus — treat both as terminal from this state machine's
        // perspective.
        case 'VERIFICATION_OK':
        case 'SIGN_IN_NEEDS_PHONE':
          return { step: 'COMPLETE' };
        case 'VERIFICATION_FAIL':
          return {
            step: 'RESET_CODE_SENT',
            identifier: state.identifier,
            pendingNewPassword: state.pendingNewPassword,
            error: action.error,
          };
        default:
          return state;
      }

    // ── COMPLETE ─────────────────────────────────────────────────────────
    case 'COMPLETE':
      switch (action.type) {
        case 'BACK':
          return INITIAL_STATE;
        default:
          return state;
      }

    default:
      return state;
  }
}
